import { flow, pipe } from 'fp-ts/lib/function';
import * as IO from 'fp-ts/lib/IO';
import * as Task from 'fp-ts/lib/Task';
import * as TE from 'fp-ts/lib/TaskEither';
import { Cause, Exit, Option, Effect as Eff } from 'effect';

import type { EffectTs, IOts, TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";

const brandIO = <Value>(io: IO.IO<Value>): IOts<Value> => io as IOts<Value>;
const brandTask = <Value>(task: Task.Task<Value>): TaskTs<Value> => task as TaskTs<Value>;
const brandTaskEither = <Error, Value>(task: TE.TaskEither<Error, Value>): TaskEitherTs<Error, Value> =>
    task as TaskEitherTs<Error, Value>;
const brandEffect = <Value, Error>(effect: Eff.Effect<Value, Error>): EffectTs<Value, Error> =>
    effect as EffectTs<Value, Error>;

type RejectionReason = Error | { message: string } | string;

const normalizeError = (reason: RejectionReason): Error => {
    if (reason instanceof Error) {
        return reason;
    }
    if (typeof reason === "string") {
        return new Error(reason);
    }
    const trimmed = reason.message.trim();
    return new Error(trimmed === "" ? "Unexpected error" : reason.message);
};

export const toRawIO = <Value>(io: IOts<Value>): IO.IO<Value> => io as IO.IO<Value>;
export const toRawTask = <Value>(task: TaskTs<Value>): Task.Task<Value> => task as Task.Task<Value>;
export const toRawTaskEither = <Error, Value>(task: TaskEitherTs<Error, Value>): TE.TaskEither<Error, Value> =>
    task as TE.TaskEither<Error, Value>;
export const toRawEffect = <Value, Error>(effect: EffectTs<Value, Error>): Eff.Effect<Value, Error> =>
    effect as Eff.Effect<Value, Error>;

export const effectFail = <Value, Error>(error: Error): EffectTs<Value, Error> => brandEffect(Eff.fail(error));
export const effectCatchAll = <Value, Error>(
    effect: EffectTs<Value, Error>,
    handler: (error: Error) => EffectTs<Value, Error>,
): EffectTs<Value, Error> =>
    brandEffect(Eff.catchAll(toRawEffect(effect), flow(handler, toRawEffect)));

export const ioEffect = <Value>(effect: () => Value): IOts<Value> => brandIO(() => effect());
export const ioOf = <Value>(value: Value): IOts<Value> => ioEffect(() => value);
export const ioMap = <Input, Output>(io: IOts<Input>, mapFn: (value: Input) => Output): IOts<Output> =>
    brandIO(() => mapFn(toRawIO(io)()));
export const ioChain = <Input, Output>(io: IOts<Input>, next: (value: Input) => IOts<Output>): IOts<Output> =>
    brandIO(() => toRawIO(next(toRawIO(io)()))());
export const ioTap = <Value, Tap>(io: IOts<Value>, tap: (value: Value) => IOts<Tap>): IOts<Value> =>
    ioChain(io, (value) => ioMap(tap(value), () => value));


export const taskFromRaw = <Value>(task: Task.Task<Value>): TaskTs<Value> => brandTask(task);
export const taskFromIO = <Value>(io: IOts<Value>): TaskTs<Value> => brandTask(Task.fromIO(toRawIO(io)));
export const taskOf = <Value>(value: Value): TaskTs<Value> => brandTask(Task.of(value));
export const taskMap = <Input, Output>(task: TaskTs<Input>, mapFn: (value: Input) => Output): TaskTs<Output> =>
    brandTask(pipe(toRawTask(task), Task.map(mapFn)));
export const taskChain = <Input, Output>(
    task: TaskTs<Input>,
    next: (value: Input) => TaskTs<Output>,
): TaskTs<Output> => brandTask(pipe(toRawTask(task), Task.chain(flow(next, toRawTask))));
export const taskTap = <Value, Tap>(task: TaskTs<Value>, tap: (value: Value) => TaskTs<Tap>): TaskTs<Value> =>
    taskChain(task, (value) => taskMap(tap(value), () => value));
export const taskTryCatch = <Value>(
    thunk: () => Promise<Value>,
    onRejected: (reason: Error) => Value,
): TaskTs<Value> =>
    brandTask(() => thunk().catch((reason: RejectionReason) => onRejected(normalizeError(reason))));
export const taskDelay = <Value>(millis: number, task: TaskTs<Value>): TaskTs<Value> =>
    brandTask(Task.delay(millis)(toRawTask(task)));

export const taskEitherFromIO = <Error, Value>(io: IOts<Value>): TaskEitherTs<Error, Value> =>
    brandTaskEither(TE.fromIO(toRawIO(io)));
export const taskEitherOf = <Value>(value: Value): TaskEitherTs<never, Value> =>
    brandTaskEither(TE.right(value));
export const taskEitherLeft = <Error>(error: Error): TaskEitherTs<Error, never> =>
    brandTaskEither(TE.left(error));
export const taskEitherFromRaw = <Error, Value>(task: TE.TaskEither<Error, Value>): TaskEitherTs<Error, Value> =>
    brandTaskEither(task);
export const taskEitherMap = <Error, Input, Output>(
    task: TaskEitherTs<Error, Input>,
    mapFn: (value: Input) => Output,
): TaskEitherTs<Error, Output> =>
    brandTaskEither(pipe(toRawTaskEither(task), TE.map(mapFn)));
export const taskEitherChain = <Error, Input, Output>(
    task: TaskEitherTs<Error, Input>,
    next: (value: Input) => TaskEitherTs<Error, Output>,
): TaskEitherTs<Error, Output> =>
    brandTaskEither(pipe(toRawTaskEither(task), TE.chain(flow(next, toRawTaskEither))));
export const taskEitherTap = <Error, Value, Tap>(
    task: TaskEitherTs<Error, Value>,
    tap: (value: Value) => TaskEitherTs<Error, Tap>,
): TaskEitherTs<Error, Value> =>
    taskEitherChain(task, (value) => taskEitherMap(tap(value), () => value));
export const taskEitherMapLeft = <Error, Value, Error2>(
    task: TaskEitherTs<Error, Value>,
    mapFn: (error: Error) => Error2,
): TaskEitherTs<Error2, Value> => brandTaskEither(pipe(toRawTaskEither(task), TE.mapLeft(mapFn)));
export const taskEitherFromTask = <ErrorType, Value>(
    task: TaskTs<Value>,
    onRejected: (reason: Error) => ErrorType,
): TaskEitherTs<ErrorType, Value> =>
    brandTaskEither(
        TE.tryCatch(toRawTask(task), (reason) => onRejected(normalizeError(reason as RejectionReason))),
    );
export const taskEitherRightTask = <Error, Value>(task: TaskTs<Value>): TaskEitherTs<Error, Value> =>
    brandTaskEither(TE.rightTask(toRawTask(task)));

export const taskEitherFold = <Error, Value, Output>(
    task: TaskEitherTs<Error, Value>,
    onLeft: (error: Error) => TaskTs<Output>,
    onRight: (value: Value) => TaskTs<Output>,
): TaskTs<Output> =>
    brandTask(
        pipe(
            toRawTaskEither(task),
            TE.fold(flow(onLeft, toRawTask), flow(onRight, toRawTask)),
        ),
    );
export const taskEitherTryCatch = <ErrorType, Value>(
    thunk: () => Promise<Value>,
    onRejected: (reason: Error) => ErrorType,
): TaskEitherTs<ErrorType, Value> =>
    brandTaskEither(TE.tryCatch(thunk, (reason) => onRejected(normalizeError(reason as RejectionReason))));

export const taskWithLoading = <Value>(
    triggerLoading: IOts<Ok>,
    work: () => TaskTs<Value>,
): TaskTs<Value> => taskChain(taskFromIO(triggerLoading), work);

export const taskEitherWithLoading = <ErrorType, Value, Output>(
    triggerLoading: IOts<Ok>,
    work: () => TaskEitherTs<ErrorType, Value>,
    onLeft: (error: ErrorType) => TaskTs<Output>,
    onRight: (value: Value) => TaskTs<Output>,
): TaskTs<Output> =>
    taskWithLoading(triggerLoading, () => taskEitherFold(work(), onLeft, onRight));

export const effectFromRaw = <Value, ErrorType>(
    effect: Eff.Effect<Value, ErrorType>,
): EffectTs<Value, ErrorType> => brandEffect(effect);
export const taskEitherFromEffect = <ErrorType, Value>(
    effect: EffectTs<Value, ErrorType>,
    onRejected: (reason: ErrorType) => ErrorType,
): TaskEitherTs<ErrorType, Value> =>
    brandTaskEither(
        TE.tryCatch(
            async () => {
                const exit = await Eff.runPromiseExit(toRawEffect(effect));
                if (Exit.isFailure(exit)) {
                    const failure = Cause.failureOption(exit.cause);
                    if (Option.isSome(failure)) {
                        return Promise.reject(failure.value);
                    }
                    return Promise.reject(Cause.squash(exit.cause));
                }
                return exit.value;
            },
            (reason) => onRejected(reason as ErrorType),
        ),
    );
export const ioFromRaw = <Value>(io: IO.IO<Value>): IOts<Value> => brandIO(io);
